import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Member } from "../../shared/models";
import { api } from "../data/api";
import { Icon } from "../ui/Icon";
import { QrCode } from "../ui/OutsideLink";
import { relativeAge } from "../ui/time";

interface PasskeyRow {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Admin → Family, inside a person's editor: invite them to sign in with a passkey
 * on their own phone (a QR code to scan, or a link to send), and see or remove
 * the passkeys they have. Works for children too.
 */
export function MemberSignIn({ member, onChanged }: { member: Member; onChanged: () => void }) {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ["passkeys", member.id], queryFn: () => api<PasskeyRow[]>(`/members/${member.id}/passkeys`) });
  const [invite, setInvite] = useState<{ url: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeInvite = async () => {
    setError(null);
    try {
      setInvite(await api<{ url: string; expiresAt: string }>(`/members/${member.id}/invite`, { method: "POST", body: {} }));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async (id: string) => {
    setError(null);
    try {
      await api(`/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" });
      void qc.invalidateQueries({ queryKey: ["passkeys", member.id] });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const copy = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="field member-signin">
      <span>Sign in on their own phone</span>
      <p className="muted small">A passkey: no password and no Google account. They scan the code with their phone, tap “Create my passkey”, and sign in with their face, fingerprint or screen lock.</p>
      {(keys.data ?? []).length > 0 && (
        <ul className="spot-admin">
          {keys.data!.map((k) => (
            <li key={k.id}>
              <Icon name="lock" size="1.1rem" />
              <strong>{k.label || "Passkey"}</strong>
              <span className="muted small">{k.lastUsedAt ? `used ${relativeAge(k.lastUsedAt)}` : `added ${relativeAge(k.createdAt)}`}</span>
              <button className="icon-btn" onClick={() => remove(k.id)} aria-label={`Remove this passkey (signs ${member.displayName} out everywhere)`} title="Remove (signs them out everywhere)">
                <Icon name="trash" size="1.1rem" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {invite ? (
        <div className="member-invite">
          <QrCode text={invite.url} className="signin-qr" label={`Invite for ${member.displayName}`} />
          <div className="member-invite-text">
            <strong>Scan with {member.displayName}’s phone</strong>
            <span className="muted small">Or send them the link. It works once and expires {new Date(invite.expiresAt).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}.</span>
            <div className="row">
              <button className="btn btn-sm" onClick={copy}>
                <Icon name="copy" size="1rem" /> {copied ? "Copied" : "Copy link"}
              </button>
              <button className="btn btn-sm" onClick={makeInvite}>
                New code
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={makeInvite}>
          <Icon name="lock" size="1.1rem" /> {(keys.data ?? []).length ? "Add another phone" : `Invite ${member.displayName} to sign in`}
        </button>
      )}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}
